import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Sparkles, X, Copy, Check, FileText, Send, Loader2, ShieldCheck, MailCheck, Eye, AlertTriangle, Lock, Smile, Briefcase, Gavel, Flame, ScanSearch, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import RichText from "@/components/RichText";
import InboundForwardCard from "@/components/InboundForwardCard";
import LockedAction from "@/components/LockedAction";
import { Link } from "react-router-dom";

// Human-friendly "X minutes after send" string, capped at days.
function relAfter(sentAt, eventAt) {
 if (!sentAt || !eventAt) return "";
 const ms = new Date(eventAt).getTime() - new Date(sentAt).getTime();
 if (!isFinite(ms) || ms < 0) return "";
 const m = Math.round(ms / 60000);
 if (m < 1) return "just now";
 if (m < 60) return `${m}m after send`;
 const h = Math.round(m / 60);
 if (h < 24) return `${h}h after send`;
 const d = Math.round(h / 24);
 return `${d}d after send`;
}

function fmtDate(iso) {
 if (!iso) return "";
 try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
}

const TONES = [
  { key: "friendly", label: "Friendly", desc: "Warm and cooperative. Keeps the relationship intact.", icon: Smile },
  { key: "professional", label: "Professional", desc: "Neutral, formal, businesslike.", icon: Briefcase },
  { key: "firm", label: "Firm", desc: "Cites the Act hard, sets deadlines, signals tribunal readiness.", icon: Gavel },
  { key: "nuclear", label: "Nuclear", desc: "Point-by-point denial plus counter-attack, backed by your evidence.", icon: Flame },
];

export default function LettersTab({ caseId, caseState, caseObj }) {
 const { user } = useAuth();
 const [templates, setTemplates] = useState([]);
 const [annexMap, setAnnexMap] = useState({});
 const [selected, setSelected] = useState(null);
 const [extra, setExtra] = useState("");
 const [busy, setBusy] = useState(false);
 const [busyPhase, setBusyPhase] = useState("");
 const [result, setResult] = useState("");
 // Preview metadata returned by the backend for free-tier users. When true,
 // we render a "TenantTrail DRAFT" watermark over the text and a blur fade
 // to the upgrade prompt. Sending is also locked.
 const [preview, setPreview] = useState(false);
 const [hiddenChars, setHiddenChars] = useState(0);
 const [copied, setCopied] = useState(false);
 const isFree = !user || user.tier === "free";
 const isPower = !!user && ["copilot", "dispute", "pro"].includes(user.tier);
 const [tone, setTone] = useState("professional");
 const [evidenceSource, setEvidenceSource] = useState("none");
 const [masterFile, setMasterFile] = useState(null);
 const [savedDrafts, setSavedDrafts] = useState([]);
 const [draftId, setDraftId] = useState(null);
 const [brief, setBrief] = useState("");
 const [briefOpen, setBriefOpen] = useState(true);
 const [masterOpen, setMasterOpen] = useState(false);
 const [rescanning, setRescanning] = useState(false);
 const freeDraftsUsed = user?.letters_drafted_lifetime || 0;
 const freeDraftsCap = user?.free_letters_lifetime_cap || 1;
 const freeDraftsExhausted = isFree && freeDraftsUsed >= freeDraftsCap && !result;
 // Refinement-loop state. versions is a stack so the user can revert.
 const [versions, setVersions] = useState([]);
 const [refineText, setRefineText] = useState("");
 const [refining, setRefining] = useState(false);

 // Email-letter modal state
 const [emailOpen, setEmailOpen] = useState(false);
 const [recipient, setRecipient] = useState("");
 const [subject, setSubject] = useState("");
 const [ccSelf, setCcSelf] = useState(true);
 const [bccInbound, setBccInbound] = useState(true);
 const [inboundAddress, setInboundAddress] = useState("");
 const [sending, setSending] = useState(false);
 const [sentInfo, setSentInfo] = useState(null);
 const [sentHistory, setSentHistory] = useState([]);

 useEffect(() => {
 api.get("/letter-templates").then(({ data }) => setTemplates(data));
 api.get(`/cases/${caseId}/annexes`).then(({ data }) => {
 const m = {};
 data.forEach(d => { m[d.annex_number] = d; });
 setAnnexMap(m);
 });
 api.get(`/cases/${caseId}/letters/sent`).then(({ data }) => setSentHistory(data.sent || [])).catch(() => {});
 api.get(`/cases/${caseId}/inbound-address`).then(({ data }) => setInboundAddress(data.address || "")).catch(() => {});
 api.get(`/cases/${caseId}/master-file`).then(({ data }) => setMasterFile(data)).catch(() => {});
 api.get(`/cases/${caseId}/letter-drafts`).then(({ data }) => setSavedDrafts(data.drafts || [])).catch(() => {});
 }, [caseId]);

 const refreshDrafts = () => {
 api.get(`/cases/${caseId}/letter-drafts`).then(({ data }) => setSavedDrafts(data.drafts || [])).catch(() => {});
 };

 const openDraft = (d) => {
 const tmpl = templates.find((t) => t.key === d.template_key) || { key: d.template_key, label: d.template_label || d.template_key, description: "" };
 setSelected(tmpl);
 setResult(d.text);
 setBrief(d.evidence_brief || "");
 setBriefOpen(false);
 setTone(d.tone || "professional");
 setDraftId(d.id);
 setVersions([]); setRefineText(""); setSuggestions([]);
 setPreview(false); setHiddenChars(0);
 };

 const deleteDraft = async (id) => {
 try {
 await api.delete(`/letter-drafts/${id}`);
 setSavedDrafts((ds) => ds.filter((d) => d.id !== id));
 if (draftId === id) setDraftId(null);
 toast("Draft deleted", { duration: 2000 });
 } catch (e) {
 toast.error("Could not delete the draft.");
 }
 };

 // Rebuild the Master File from the panel (1 AI query).
 const rescanMasterFile = async () => {
 setRescanning(true);
 try {
 const { data } = await api.post(`/cases/${caseId}/evidence-brief-scan`);
 setMasterFile({ exists: true, brief: data.brief, built_at: data.built_at, stale_count: 0 });
 toast.success("Master File rebuilt from your latest evidence.");
 } catch (e) {
 toast.error(e.response?.data?.detail || "Deep Scan failed.");
 } finally { setRescanning(false); }
 };

 const generate = async () => {
 if (!selected) return;
 setBusy(true); setResult(""); setVersions([]); setRefineText(""); setSuggestions([]);
 setPreview(false); setHiddenChars(0); setBrief(""); setDraftId(null);
 try {
 let briefText = "";
 if (evidenceSource === "master" && masterFile?.exists) {
 briefText = masterFile.brief || "";
 setBrief(briefText);
 setBriefOpen(false);
 }
 if (evidenceSource === "scan" && isPower) {
 setBusyPhase("scanning");
 const { data: scan } = await api.post(`/cases/${caseId}/evidence-brief-scan`);
 briefText = scan.brief || "";
 setBrief(briefText);
 setBriefOpen(true);
 setMasterFile({ exists: true, brief: briefText, built_at: scan.built_at, stale_count: 0 });
 }
 setBusyPhase("drafting");
 const fd = new FormData();
 fd.append("template_key", selected.key);
 fd.append("extra_context", extra);
 fd.append("tone", tone);
 fd.append("evidence_brief", briefText);
 const { data } = await api.post(`/cases/${caseId}/draft-letter`, fd, {
 headers: { "Content-Type": "multipart/form-data" }
 });
 setResult(data.text);
 setDraftId(data.draft_id || null);
 refreshDrafts();
 setPreview(!!data.preview);
 setHiddenChars(data.hidden_chars || 0);
 } catch (e) {
 alert(e.response?.data?.detail || "Draft failed");
 } finally { setBusy(false); setBusyPhase(""); }
 };

 // Iterative refinement. Pushes the current draft onto versions[] so the
 // user can revert if Compass made things worse. Each call uses one AI
 // query against the user's quota (same as generate).
 const refine = async () => {
 if (!result || !refineText.trim()) return;
 setRefining(true);
 try {
 const fd = new FormData();
 fd.append("previous_draft", result);
 fd.append("instruction", refineText);
 fd.append("tone", tone);
 fd.append("draft_id", draftId || "");
 const { data } = await api.post(`/cases/${caseId}/refine-letter`, fd, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 setVersions(v => [...v, result]);
 setResult(data.text);
 refreshDrafts();
 setPreview(!!data.preview);
 setHiddenChars(data.hidden_chars || 0);
 setRefineText("");
 } catch (e) {
 alert(e.response?.data?.detail || "Refinement failed");
 } finally { setRefining(false); }
 };

 // Ask Compass to suggest 3 specific tweaks for the current draft.
 // Tapping a chip prefills the refinement textbox so the user can edit
 // before sending. Counts as 1 AI query.
 const [suggestions, setSuggestions] = useState([]);
 const [suggesting, setSuggesting] = useState(false);
 const fetchSuggestions = async () => {
 if (!result) return;
 setSuggesting(true);
 try {
 const fd = new FormData();
 fd.append("previous_draft", result);
 const { data } = await api.post(`/cases/${caseId}/suggest-letter-refinements`, fd, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 setSuggestions(data.suggestions || []);
 } catch (e) {
 alert(e.response?.data?.detail || "Could not fetch suggestions");
 } finally { setSuggesting(false); }
 };

 const revertOne = () => {
 if (!versions.length) return;
 const prev = versions[versions.length - 1];
 setVersions(v => v.slice(0, -1));
 setResult(prev);
 };

 const copy = () => {
 navigator.clipboard.writeText(result);
 setCopied(true);
 setTimeout(() => setCopied(false), 1500);
 };

 const openEmail = () => {
 // Pre-fill the recipient from the case so the tenant doesn't retype the
 // agent's email every time they send a letter.
 setRecipient((caseObj?.agent_email || "").trim());
 // Smart subject: "[12 Marlow Cres] Repair request, non-urgent"
 // Falls back to the old default when no address is set.
 const shortAddr = (caseObj?.address || "").split(",")[0].trim().slice(0, 40);
 const tmplLabel = selected?.label || "Letter";
 const subj = shortAddr
 ? `[${shortAddr}] ${tmplLabel}`
 : `${tmplLabel}, from your tenant`;
 setSubject(subj.slice(0, 200));
 setSentInfo(null);
 setEmailOpen(true);
 };

 const sendEmail = async () => {
 if (!recipient || !recipient.includes("@")) { alert("Enter the agent's email"); return; }
 setSending(true);
 try {
 const { data } = await api.post(`/cases/${caseId}/letters/email`, {
 recipient_email: recipient,
 subject,
 body: result,
 cc_self: ccSelf,
 bcc_inbound: bccInbound,
 });
 setSentInfo(data);
 // refresh sent history
 api.get(`/cases/${caseId}/letters/sent`).then(({ data }) => setSentHistory(data.sent || [])).catch(() => {});
 // Subtle confirmation toast that the letter was filed to the chronology
 // automatically. 5-second Undo deletes the auto-added timeline event
 // (the audit-trail / letter_sends record always survives).
 const eventId = data.timeline_event_id;
 if (eventId) {
 toast.success("Letter saved to your chronology", {
 description: "Added to the timeline with the audit hash.",
 duration: 5000,
 action: {
 label: "Undo",
 onClick: async () => {
 try {
 await api.delete(`/timeline/${eventId}`);
 toast("Timeline entry removed", { duration: 2500 });
 } catch (err) {
 toast.error("Could not undo. Please remove the timeline event manually.");
 }
 },
 },
 });
 }
 } catch (e) {
 const detail = e.response?.data?.detail || "";
 if (e.response?.status === 503) {
 alert("Email sending is in early access. we'll let you know when it goes live. (RESEND_API_KEY not configured yet.)");
 } else {
 alert(detail || "Couldn't send. Try again in a moment.");
 }
 } finally { setSending(false); }
 };

 return (
 <div className="space-y-6" data-testid="letters-tab">
 <InboundForwardCard caseId={caseId} variant="full" />
 <div className="card bg-[#E6F5F2]/40">
 <div className="flex items-start gap-3">
 <Mail size={20} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5" />
 <div className="flex-1">
 <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Notice &amp; breach letter drafts</h3>
 <p className="text-sm text-[#333] leading-relaxed">
 Pick a template. the AI will draft a polished letter using your case details and the relevant
 section of the {caseState} Act. Always review and edit before sending.
 </p>
 </div>
 </div>
 </div>

 {!selected && (
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
 {templates.map((t) => (
 <button key={t.key} onClick={() => { setSelected(t); setResult(""); setExtra(""); setBrief(""); setDraftId(null); }}
 className="card card-hover text-left" data-testid={`letter-template-${t.key}`}>
 <FileText size={18} strokeWidth={1.5} className="text-[#1B6C62] mb-3" />
 <h4 className="font-serif text-lg text-[#0D2B52] mb-1">{t.label}</h4>
 <p className="text-xs text-[#6B7280] leading-relaxed">{t.description}</p>
 </button>
 ))}
 </div>
 )}

 {!selected && isPower && masterFile?.exists && (
 <div className="card" data-testid="master-file-panel">
 <div className="flex items-start justify-between gap-3 flex-wrap">
 <div className="flex items-start gap-2.5">
 <ScanSearch size={18} strokeWidth={1.5} className="text-[#8A6D3B] mt-0.5" />
 <div>
 <h4 className="font-serif text-lg text-[#0D2B52] inline-flex items-center gap-2 flex-wrap">
 Case Master File
 {masterFile.stale_count > 0 && (
 <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[#B35D4C] bg-[#FCE9E5] border border-[#B35D4C]/40 px-1.5 py-0.5" data-testid="master-file-stale-badge">
 {masterFile.stale_count} new item{masterFile.stale_count === 1 ? "" : "s"} since last scan
 </span>
 )}
 </h4>
 <p className="text-xs text-[#6B7280] mt-0.5">
 Every fact, date and finding from your last Deep Scan, built {fmtDate(masterFile.built_at)}. Letters can reference it instantly without spending AI queries.
 </p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <button onClick={() => setMasterOpen((v) => !v)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="master-file-view-btn">
 <Eye size={12} strokeWidth={1.5} /> {masterOpen ? "Hide" : "View"}
 </button>
 <button onClick={rescanMasterFile} disabled={rescanning} className="btn-teal text-xs inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="master-file-rebuild-btn">
 {rescanning ? (<><Loader2 size={12} strokeWidth={1.5} className="animate-spin" /> Scanning…</>) : (<><Sparkles size={12} strokeWidth={1.5} /> Rebuild (1 AI query)</>)}
 </button>
 </div>
 </div>
 {masterOpen && (
 <div className="mt-4 border-t border-[#E5E4DE] pt-4 whitespace-pre-wrap font-serif text-[13.5px] leading-relaxed text-[#1A1A1A]" data-testid="master-file-body">
 <RichText text={masterFile.brief} annexMap={annexMap} state={caseState} />
 </div>
 )}
 </div>
 )}

 {!selected && savedDrafts.length > 0 && (
 <div className="card" data-testid="saved-drafts">
 <div className="flex items-center gap-2 mb-1">
 <FileText size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
 <h4 className="font-serif text-lg text-[#0D2B52]">Saved drafts</h4>
 </div>
 <p className="text-xs text-[#6B7280] mb-4 italic">Every letter you generate is saved here automatically. Open one to keep refining or send it.</p>
 <div className="space-y-2">
 {savedDrafts.map((d) => (
 <div key={d.id} className="border border-[#E5E4DE] p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`saved-draft-row-${d.id}`}>
 <div className="min-w-0 flex-1">
 <div className="flex items-center gap-2 flex-wrap">
 <span className="font-medium text-sm text-[#0D2B52]">{d.template_label || d.template_key}</span>
 <span className={`font-mono text-[9.5px] uppercase tracking-[0.1em] px-1.5 py-0.5 border ${d.tone === "nuclear" ? "text-[#B35D4C] bg-[#FCE9E5] border-[#B35D4C]/40" : "text-[#1B6C62] bg-[#E6F5F2] border-[#1B6C62]/30"}`}>
 {d.tone || "professional"}
 </span>
 {d.evidence_brief && (
 <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[#8A6D3B] bg-[#FAF6E8] border border-[#C9A961]/40 px-1.5 py-0.5">deep scan</span>
 )}
 </div>
 <div className="text-xs text-[#6B7280] font-mono mt-1">updated {fmtDate(d.updated_at)}</div>
 <div className="text-xs text-[#6B7280] mt-1 truncate">{(d.text || "").replace(/[#*_>`]/g, "").slice(0, 110)}…</div>
 </div>
 <div className="flex items-center gap-2 shrink-0">
 <button onClick={() => openDraft(d)} className="btn-teal text-xs" data-testid={`open-draft-${d.id}`}>Open</button>
 <button onClick={() => deleteDraft(d.id)} className="btn-ghost text-xs text-[#B35D4C]" data-testid={`delete-draft-${d.id}`}>Delete</button>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {selected && (
 <div className="card" data-testid="letter-form">
 <div className="flex justify-between items-center mb-4">
 <div>
 <div className="label-mono mb-1">Selected template</div>
 <h3 className="font-serif text-2xl text-[#0D2B52]">{selected.label}</h3>
 </div>
 <button onClick={() => { setSelected(null); setResult(""); setBrief(""); setDraftId(null); }} className="btn-ghost p-1.5" data-testid="back-to-templates"><X size={18} strokeWidth={1.5} /></button>
 </div>
 <p className="text-sm text-[#6B7280] mb-4">{selected.description}</p>

 <label className="label-mono block mb-1.5">How should this letter sound?</label>
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="letter-tone-selector">
 {TONES.map((t) => {
 const Icon = t.icon;
 const isNuclear = t.key === "nuclear";
 const lockedTone = isNuclear && !isPower;
 const active = tone === t.key;
 const Chip = lockedTone ? "span" : "button";
 const chip = (
 <Chip
 {...(lockedTone ? {} : { type: "button", onClick: () => setTone(t.key), "data-testid": `letter-tone-${t.key}` })}
 className={`block w-full h-full text-left border p-2.5 transition-colors ${
 active
 ? (isNuclear ? "border-[#B35D4C] bg-[#FCE9E5]" : "border-[#1B6C62] bg-[#E6F5F2]")
 : "border-[#E5E4DE] bg-white hover:border-[#1B6C62]/50"
 } ${lockedTone ? "pointer-events-none" : ""}`}
 >
 <span className={`inline-flex items-center gap-1.5 text-[13px] font-medium ${isNuclear ? "text-[#B35D4C]" : "text-[#0D2B52]"}`}>
 <Icon size={14} strokeWidth={1.75} /> {t.label}
 </span>
 <span className="block text-[11px] text-[#6B7280] leading-snug mt-1">{t.desc}</span>
 </Chip>
 );
 return lockedTone ? (
 <LockedAction key={t.key} locked reason="letter_tone_nuclear" tier="copilot" label="Nuclear tone" testid="letter-tone-nuclear-locked">
 {chip}
 </LockedAction>
 ) : (
 <React.Fragment key={t.key}>{chip}</React.Fragment>
 );
 })}
 </div>

 <div className="mb-4">
 <label className="label-mono block mb-1.5">What should this letter stand on?</label>
 {isPower ? (
 <div className="space-y-2" data-testid="evidence-source-selector">
 {[
 { key: "none", title: "Quick draft", desc: "Uses your case details plus the facts you type below. No extra AI queries." },
 {
 key: "master",
 title: "Reference Master File",
 desc: masterFile?.exists
 ? `Your saved evidence brief, built ${fmtDate(masterFile.built_at)}. Instant, no extra AI queries.`
 : "No Master File yet. Run a Fresh Deep Scan once to create it.",
 disabled: !masterFile?.exists,
 badge: masterFile?.exists && masterFile.stale_count > 0 ? `${masterFile.stale_count} new item${masterFile.stale_count === 1 ? "" : "s"} since last scan` : "",
 },
 { key: "scan", title: "Fresh Deep Scan", desc: "Re-reads every document, Deep Read, timeline entry and move-out flag, then rebuilds your Master File. Uses 1 extra AI query." },
 ].map((opt) => {
 const active = evidenceSource === opt.key;
 return (
 <button
 type="button"
 key={opt.key}
 disabled={opt.disabled}
 onClick={() => setEvidenceSource(opt.key)}
 className={`w-full flex items-start gap-2.5 text-left border p-3 transition-colors ${
 active ? "border-[#C9A961] bg-[#FAF6E8]" : "border-[#E5E4DE] bg-white hover:border-[#C9A961]/60"
 } ${opt.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
 data-testid={`evidence-source-${opt.key}`}
 >
 <span className={`mt-1 w-3.5 h-3.5 rounded-full border flex-shrink-0 ${active ? "border-[#8A6D3B] bg-[#8A6D3B]" : "border-[#9CA3AF]"}`} aria-hidden="true" />
 <span className="text-sm text-[#0D2B52] font-serif leading-snug">
 <strong className="inline-flex items-center gap-1.5">
 {opt.key !== "none" && <ScanSearch size={13} strokeWidth={1.75} className="text-[#8A6D3B]" />} {opt.title}
 {opt.badge && (
 <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[#B35D4C] bg-[#FCE9E5] border border-[#B35D4C]/40 px-1.5 py-0.5" data-testid="master-stale-badge">{opt.badge}</span>
 )}
 </strong>
 <span className="block text-xs text-[#6B7280] mt-0.5 italic">{opt.desc}</span>
 </span>
 </button>
 );
 })}
 </div>
 ) : (
 <LockedAction locked reason="letter_deep_scan" tier="copilot" label="Deep Evidence Scan" testid="deep-scan-locked">
 <span className="flex items-start gap-2.5 border border-[#E5E4DE] bg-white p-3 pointer-events-none text-left">
 <span className="text-sm text-[#0D2B52] font-serif leading-snug">
 <strong className="inline-flex items-center gap-1.5"><ScanSearch size={14} strokeWidth={1.75} className="text-[#8A6D3B]" /> Deep Evidence Scan &amp; Master File</strong>
 <span className="block text-xs text-[#6B7280] mt-0.5 italic">
 Compass reads your whole case first, keeps a Master File of every fact and date, then drafts letters grounded in your strongest evidence.
 </span>
 </span>
 </span>
 </LockedAction>
 )}
 </div>

 <label className="label-mono block mb-1.5">Your specific facts (optional but recommended)</label>
 <textarea
 rows={4} className="input-field mb-4"
 placeholder="E.g. The hot water has been broken since 12 March 2026. I've reported it 3 times by email. Last contact was 28 March with no response."
 value={extra} onChange={(e) => setExtra(e.target.value)}
 data-testid="letter-context" />

 {freeDraftsExhausted ? (
 <LockedAction
 locked={true}
 reason="letter_more"
 tier="copilot"
 label="Generate letter"
 testid="generate-letter-locked"
 >
 <span className="btn-primary w-full inline-flex items-center justify-center gap-2 pointer-events-none">
 <Sparkles size={15} strokeWidth={1.5} /> Generate letter
 </span>
 </LockedAction>
 ) : (
 <button onClick={generate} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="generate-letter">
 <Sparkles size={15} strokeWidth={1.5} /> {busy ? (busyPhase === "scanning" ? "Deep scanning your whole case…" : "Drafting (our AI is reading the Act)…") : (isFree ? "Generate your one free letter" : "Generate letter")}
 </button>
 )}
 {isFree && !freeDraftsExhausted && !result && (
 <p className="text-[11.5px] text-[#6B7280] mt-2 text-center font-serif italic" data-testid="letter-free-hint">
 Free plan: one letter draft to feel the quality. Refine it as much as you like. Sending or drafting more letters is on Compass.
 </p>
 )}

 {result && (
 <div className="mt-6 border-t border-[#E5E4DE] pt-6" data-testid="letter-result">
 {brief && (
 <div className="mb-4 border border-[#C9A961]/50 bg-[#FAF6E8]" data-testid="evidence-brief">
 <button
 type="button"
 onClick={() => setBriefOpen((v) => !v)}
 className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left"
 data-testid="evidence-brief-toggle"
 >
 <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8A6D3B]">
 <ScanSearch size={13} strokeWidth={1.75} /> Evidence brief · what this letter stands on
 </span>
 <ChevronDown size={14} strokeWidth={1.75} className={`text-[#8A6D3B] transition-transform ${briefOpen ? "rotate-180" : ""}`} />
 </button>
 {briefOpen && (
 <div className="px-4 pb-4">
 <div className="whitespace-pre-wrap font-serif text-[13.5px] leading-relaxed text-[#1A1A1A]" data-testid="evidence-brief-body">
 <RichText text={brief} annexMap={annexMap} state={caseState} />
 </div>
 <p className="text-[11px] text-[#8A6D3B] italic mt-3">Built from a deep scan of your documents, Deep Reads, timeline and move-out flags. The letter below leans on the strongest points.</p>
 </div>
 )}
 </div>
 )}
 <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
 <span className="label-mono">Your draft letter{preview ? " · preview" : ""}</span>
 <div className="flex items-center gap-2">
 {preview ? (
 <LockedAction locked={true} reason="letter_full" tier="copilot" label="Copy" inline testid="copy-letter-locked">
 <span className="btn-ghost text-xs inline-flex items-center gap-1 pointer-events-none">
 <Copy size={12} strokeWidth={1.5} /> Copy
 </span>
 </LockedAction>
 ) : (
 <button onClick={copy} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="copy-letter">
 {copied ? (<><Check size={12} strokeWidth={2} /> Copied</>) : (<><Copy size={12} strokeWidth={1.5} /> Copy</>)}
 </button>
 )}
 {isFree ? (
 <LockedAction locked={true} reason="letter_send" tier="copilot" label="Email with audit stamp" inline testid="email-letter-locked">
 <span className="btn-primary text-xs inline-flex items-center gap-1.5 pointer-events-none">
 <Send size={12} strokeWidth={1.5} /> Email with audit stamp
 </span>
 </LockedAction>
 ) : (
 <button onClick={openEmail} className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid="email-letter">
 <Send size={12} strokeWidth={1.5} /> Email with audit stamp
 </button>
 )}
 </div>
 </div>
 <div className="relative whitespace-pre-wrap font-serif text-[15px] leading-relaxed text-[#1A1A1A] bg-white border border-[#E5E4DE] p-6" data-testid="letter-text-body">
 <RichText text={result} annexMap={annexMap} state={caseState} />
 {preview && (
 <>
 {/* Diagonal "TENANTTRAIL DRAFT" watermark — sits in the text box bounding so any screenshot keeps it. */}
 <div
 className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden select-none"
 aria-hidden="true"
 data-testid="letter-watermark"
 >
 <span
 className="font-mono uppercase text-[#1B6C62]/20 tracking-[0.3em] whitespace-nowrap"
 style={{ transform: "rotate(-22deg)", fontSize: "44px", letterSpacing: "0.3em" }}
 >
 TenantTrail · Draft · TenantTrail · Draft
 </span>
 </div>
 {/* Blur fade at the bottom of the visible preview, ending in the upgrade card. */}
 <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white" aria-hidden="true" />
 </>
 )}
 </div>
 {preview && (
 <div className="mt-3 border-2 border-dashed border-[#B35D4C]/40 bg-[#FFF7F2] px-4 py-4" data-testid="letter-preview-upgrade">
 <div className="flex items-start gap-3">
 <div className="w-8 h-8 bg-[#B35D4C]/10 border border-[#B35D4C]/30 flex items-center justify-center flex-shrink-0">
 <Lock size={14} strokeWidth={1.75} className="text-[#B35D4C]" />
 </div>
 <div className="flex-1">
 <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#B35D4C] mb-0.5">
 Free preview · about 35 percent shown
 </div>
 <div className="font-serif text-[15px] text-[#0D2B52] leading-snug mb-1">
 The legal citations, your specific asks plus the closing arrive with Compass.
 </div>
 <p className="text-[12.5px] text-[#1A1A1A] leading-snug mb-3">
 We hold back the parts the agent reads first. {hiddenChars > 0 ? `About ${hiddenChars.toLocaleString()} characters are waiting on the other side.` : ""}
 </p>
 <Link to="/pricing?upgrade=letter_full" className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-[0.12em] text-white bg-[#1B6C62] px-4 py-2 hover:opacity-90" data-testid="letter-preview-upgrade-cta">
 See the full draft →
 </Link>
 </div>
 </div>
 </div>
 )}
 <p className="text-xs text-[#6B7280] mt-3 italic">Review carefully before sending. Check dates, section references and any &quot;[bracketed]&quot; placeholders. This is general drafting help, not legal advice.</p>

 {/* Refine the letter, back-and-forth with Compass. */}
 <div className="mt-6 border-t border-[#E5E4DE] pt-5" data-testid="letter-refine-section">
 <div className="flex items-center justify-between gap-2 mb-2">
 <div>
 <h4 className="font-serif text-[16px] text-[#0D2B52]">Not quite right? Tell Compass what to change.</h4>
 <p className="text-[12px] text-[#5a6b7a]">
 Like, &ldquo;add a paragraph about the broken oven&rdquo; or &ldquo;make this firmer&rdquo; or &ldquo;take out the bit about rent arrears&rdquo;. Each tweak counts as one chat.
 </p>
 </div>
 {versions.length > 0 && (
 <button onClick={revertOne} className="btn-ghost text-xs inline-flex items-center gap-1 shrink-0" data-testid="letter-revert-btn" title="Go back to the previous version">
 &larr; Undo (v{versions.length})
 </button>
 )}
 </div>
 {/* Suggestion chips, tap to prefill the refinement input */}
 <div className="mb-3" data-testid="letter-suggestions-block">
 {suggestions.length === 0 ? (
 <button
 onClick={fetchSuggestions}
 disabled={suggesting}
 className="text-xs inline-flex items-center gap-1.5 text-[#1B6C62] hover:underline disabled:opacity-60"
 data-testid="letter-suggest-btn"
 >
 {suggesting ? (<><Loader2 size={11} strokeWidth={1.5} className="animate-spin" /> Reading your draft…</>) : (<><Sparkles size={11} strokeWidth={1.5} /> Suggest 3 tweaks (uses 1 AI query)</>)}
 </button>
 ) : (
 <div data-testid="letter-suggestions">
 <div className="text-[11px] text-[#6B7280] font-mono mb-2">Tap a suggestion to prefill the box below.</div>
 <div className="flex flex-wrap gap-1.5">
 {suggestions.map((s, i) => (
 <button
 key={i}
 type="button"
 onClick={() => setRefineText(s)}
 className="text-left text-xs px-2.5 py-1.5 border border-[#1B6C62]/30 bg-[#E6F5F2] text-[#0D2B52] hover:bg-[#1B6C62] hover:text-white transition-colors"
 data-testid={`letter-suggestion-${i}`}
 >
 {s}
 </button>
 ))}
 <button
 type="button"
 onClick={() => setSuggestions([])}
 className="text-[10px] text-[#6B7280] hover:underline px-1"
 title="Hide suggestions"
 >
 dismiss
 </button>
 </div>
 </div>
 )}
 </div>
 <textarea
 value={refineText}
 onChange={e => setRefineText(e.target.value)}
 placeholder="What should Compass change?"
 rows={2}
 maxLength={2000}
 className="input-field font-serif"
 data-testid="letter-refine-input"
 disabled={refining}
 />
 <div className="flex items-center justify-between gap-3 mt-2">
 <span className="text-[11px] text-[#9CA3AF] font-mono">{refineText.length}/2000</span>
 <button
 onClick={refine}
 disabled={refining || !refineText.trim()}
 className="btn-teal text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
 data-testid="letter-refine-btn"
 >
 {refining ? (<><Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> Refining</>) : (<><Sparkles size={13} strokeWidth={1.5} /> Refine letter</>)}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 )}

 {/* Sent letters audit trail */}
 {sentHistory.length > 0 && !selected && (
 <div className="card" data-testid="sent-history">
 <div className="flex items-center gap-2 mb-3">
 <ShieldCheck size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
 <h4 className="font-serif text-lg text-[#0D2B52]">Sent letters · audit trail</h4>
 </div>
 <p className="text-xs text-[#6B7280] mb-4 italic">Every letter you email through TenantTrail is timestamped and hashed. If a dispute escalates, this list is your proof.</p>
 <div className="space-y-2">
 {sentHistory.map((s) => (
 <div key={s.id} className="border border-[#E5E4DE] p-3 text-sm" data-testid={`sent-row-${s.id}`}>
 <div className="flex justify-between flex-wrap gap-2">
 <div className="min-w-0">
 <div className="font-medium text-[#0D2B52]">{s.subject}</div>
 <div className="text-xs text-[#6B7280] font-mono">to {s.recipient_email} · {s.sent_at?.slice(0,19).replace("T", " ")}</div>
 </div>
 <div className="font-mono text-[10px] text-[#6B7280] break-all max-w-xs">hash: {s.audit_hash?.slice(0, 16)}…</div>
 </div>
 <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`sent-row-events-${s.id}`}>
 {s.delivered_at && (
 <span className="inline-flex items-center gap-1 text-[11px] font-mono bg-[#E6F5F2] text-[#1B6C62] border border-[#1B6C62]/30 px-2 py-0.5" data-testid={`sent-event-delivered-${s.id}`}>
 <MailCheck size={10} strokeWidth={2} /> Delivered · {relAfter(s.sent_at, s.delivered_at)}
 </span>
 )}
 {s.opened_at && (
 <span className="inline-flex items-center gap-1 text-[11px] font-mono bg-[#FAF6E8] text-[#8A6D3B] border border-[#C9A961]/40 px-2 py-0.5" data-testid={`sent-event-opened-${s.id}`}>
 <Eye size={10} strokeWidth={2} /> Opened · {relAfter(s.sent_at, s.opened_at)}
 </span>
 )}
 {s.bounced_at && (
 <span className="inline-flex items-center gap-1 text-[11px] font-mono bg-[#FCE9E5] text-[#B35D4C] border border-[#B35D4C]/40 px-2 py-0.5" data-testid={`sent-event-bounced-${s.id}`}>
 <AlertTriangle size={10} strokeWidth={2} /> Bounced · {relAfter(s.sent_at, s.bounced_at)}
 </span>
 )}
 {!s.delivered_at && !s.opened_at && !s.bounced_at && (
 <span className="text-[11px] text-[#6B7280] font-serif italic">Awaiting delivery confirmation…</span>
 )}
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Email modal */}
 {emailOpen && (
 <div className="fixed inset-0 z-50 bg-[#0D2B52]/70 flex items-center justify-center p-4" onClick={() => setEmailOpen(false)}>
 <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full shadow-2xl" data-testid="email-modal">
 <div className="px-5 py-3 border-b border-[#E5E4DE] flex items-center justify-between">
 <div className="flex items-center gap-2">
 <Send size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
 <h3 className="font-serif text-lg text-[#0D2B52]">Email with audit stamp</h3>
 </div>
 <button onClick={() => setEmailOpen(false)} className="btn-ghost p-1"><X size={16} strokeWidth={1.5} /></button>
 </div>
 <div className="px-5 py-4 space-y-3">
 {!sentInfo && (
 <>
 <p className="text-sm text-[#333] font-serif leading-relaxed">
 We&apos;ll send the letter from a verified TenantTrail address. The recipient gets a copy in plain text PLUS a PDF attachment with a unique audit hash. The hash + timestamp are stored so you can prove later, at tribunal if needed, that this exact letter was sent on this exact date.
 </p>
 <div>
 <label className="label-mono block mb-1.5">Agent&apos;s email</label>
 <input className="input-field" type="email" placeholder="agent@example.com"
 value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="email-recipient" />
 </div>
 <div>
 <label className="label-mono block mb-1.5">Subject</label>
 <input className="input-field" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" />
 </div>
 <label className="flex items-start gap-2.5 cursor-pointer bg-[#E6F5F2] border border-[#1B6C62]/30 p-3" data-testid="email-cc-self-wrapper">
 <input type="checkbox" checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)}
 className="mt-0.5 accent-[#1B6C62]" data-testid="email-cc-self" />
 <span className="text-sm text-[#0D2B52] font-serif leading-snug">
 <strong>Also CC me</strong> at {user?.email || "your email"}
 <span className="block text-xs text-[#6B7280] mt-0.5 italic">Recommended: gives you an independent timestamp from your own mail server. Much harder to dispute at tribunal than a server-side log alone.</span>
 </span>
 </label>
 <label className="flex items-start gap-2.5 cursor-pointer bg-[#FAF6E8] border border-[#C9A961]/30 p-3" data-testid="email-bcc-inbound-wrapper">
 <input type="checkbox" checked={bccInbound} onChange={(e) => setBccInbound(e.target.checked)}
 className="mt-0.5 accent-[#1B6C62]" data-testid="email-bcc-inbound" />
 <span className="text-sm text-[#0D2B52] font-serif leading-snug">
 <strong>Auto-capture the agent&apos;s reply</strong>
 <span className="block text-xs text-[#6B7280] mt-0.5 italic">
 BCCs your private TenantTrail address {inboundAddress && (<code className="font-mono text-[10px] text-[#0D2B52]">{inboundAddress}</code>)}. When the agent hits Reply All, their response lands straight on your chronology. Recipient won&apos;t see this address.
 </span>
 </span>
 </label>
 <p className="text-[11px] text-[#8A6D3B] italic font-serif leading-relaxed">
 Note: while we finish verifying our sending domain, emails arrive from a TenantTrail-branded address. The audit hash and timestamp work the same way.
 </p>
 </>
 )}
 {sentInfo && (
 <div className="text-center py-2">
 <ShieldCheck size={28} strokeWidth={1.5} className="mx-auto text-[#1B6C62] mb-2" />
 <p className="font-serif text-lg text-[#0D2B52] mb-2">Letter sent. Audit stamped.</p>
 <p className="text-xs text-[#6B7280] font-mono mb-1">Sent at: {sentInfo.sent_at?.slice(0,19).replace("T"," ")} UTC</p>
 {sentInfo.cc?.length > 0 && (
 <p className="text-xs text-[#1B6C62] font-serif mb-1" data-testid="email-cc-confirmation">
 A copy has been sent to {sentInfo.cc.join(", ")}
 </p>
 )}
 {sentInfo.bcc?.length > 0 && (
 <p className="text-xs text-[#8A6D3B] font-serif mb-1" data-testid="email-bcc-confirmation">
 Replies will land here automatically.
 </p>
 )}
 <p className="text-[10px] text-[#6B7280] font-mono break-all">hash: {sentInfo.audit_hash}</p>
 </div>
 )}
 </div>
 <div className="px-5 py-3 border-t border-[#E5E4DE] flex justify-end gap-2">
 {!sentInfo ? (
 <>
 <button onClick={() => setEmailOpen(false)} className="btn-ghost text-sm">Cancel</button>
 <button onClick={sendEmail} disabled={sending || !recipient}
 className="btn-primary text-sm inline-flex items-center gap-2" data-testid="email-send-confirm">
 {sending ? (<><Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> Sending…</>) : (<><Send size={13} strokeWidth={1.5} /> Send</>)}
 </button>
 </>
 ) : (
 <button onClick={() => setEmailOpen(false)} className="btn-primary text-sm">Done</button>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
